import { FilterBar } from '../components/Filters';
import { StackedBarChart, MiniBar, type Series } from '../components/charts';
import {
  CAT_COLORS,
  downloadCsv,
  localTime,
  num,
  pct,
  useApi,
  useFilters,
  useQuery,
  type FailedResponse,
  type FiltersResponse,
} from '../lib/reports';

const FAIL_SERIES: Series[] = [{ key: 'failed', label: 'Failed', color: CAT_COLORS.late }];

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
}

export function FailedDeliveries() {
  const [filters, patch] = useFilters();
  const qs = useQuery(filters);
  const { data, error, loading } = useApi<FailedResponse>(`/api/reports/failed-deliveries?${qs}`);
  const { data: options } = useApi<FiltersResponse>('/api/reports/difot/filters');

  const maxReason = Math.max(1, ...(data?.by_reason.map((r) => r.n) ?? [1]));

  function exportRows() {
    downloadCsv(
      `amart-failed-${filters.from}_${filters.to}.csv`,
      ['stop_id', 'trip_date', 'driver', 'store', 'region', 'address', 'failed_time', 'failed_reason', 'detail'],
      (data?.rows ?? []).map((r) => [
        r.stop_id,
        r.trip_date,
        r.driver_name,
        r.customer_name,
        r.region,
        r.address,
        r.failed_time,
        r.failed_reason,
        r.failed_detail,
      ]),
    );
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">Reporting / Failed deliveries</p>
          <h1>Failed deliveries</h1>
          <p className="muted">
            {filters.from} → {filters.to}
          </p>
        </div>
      </header>

      <FilterBar filters={filters} options={options} onChange={patch} onExport={exportRows} />

      {error && <p className="form-error" role="alert">{error}</p>}
      {loading && !data && <p className="muted">Loading…</p>}

      {data && (
        <>
          <div className="tiles">
            <Tile label="Failed" value={num(data.summary.failed)} sub={pct(data.summary.failed_pct, 2)} color={CAT_COLORS.late} />
            <Tile label="Completed" value={num(data.summary.completed)} color={CAT_COLORS.on_time} />
            <Tile label="Total stops" value={num(data.summary.total_stops)} color={CAT_COLORS.early} />
            <Tile label="Failure rate" value={pct(data.summary.failed_pct, 2)} color={CAT_COLORS.no_eta} />
          </div>

          <div className="grid-2">
            <div className="card">
              <p className="eyebrow">Trend</p>
              <h2>Failures per day</h2>
              <StackedBarChart data={data.trend} series={FAIL_SERIES} xKey="day" formatX={formatDay} />
            </div>

            <div className="card">
              <p className="eyebrow">Breakdown</p>
              <h2>Top failure reasons</h2>
              {data.by_reason.length === 0 ? (
                <p className="muted">No failures in this period.</p>
              ) : (
                <ul className="reason-list">
                  {data.by_reason.map((r) => (
                    <li key={r.reason}>
                      <span className="reason-name" title={r.reason}>{r.reason}</span>
                      <MiniBar value={r.n} max={maxReason} color={CAT_COLORS.late} />
                      <span className="reason-count">{num(r.n)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="card">
            <h2>Failed stops</h2>
            <p className="muted small">Showing {num(data.rows.length)} of {num(data.summary.failed)}</p>
            {data.rows.length === 0 ? (
              <p className="muted">No failed stops match the current filters.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Trip date</th>
                      <th>Driver</th>
                      <th>Store</th>
                      <th>Region</th>
                      <th>Failed at</th>
                      <th>Reason</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.stop_id}>
                        <td>{r.trip_date ?? '—'}</td>
                        <td>{r.driver_name ?? '—'}</td>
                        <td>{r.customer_name ?? '—'}</td>
                        <td>{r.region ?? '—'}</td>
                        <td>{localTime(r.failed_time, null)}</td>
                        <td>
                          <span className="cat cat-late">{r.failed_reason ?? '—'}</span>
                        </td>
                        <td className="cell-addr">{r.failed_detail ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function Tile({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="tile static" style={{ borderTopColor: color }}>
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value}</span>
      {sub && <span className="tile-sub">{sub}</span>}
    </div>
  );
}
